export interface GuideSessionState {
  projectId: string;
  activityId: string;
  step: number;
  totalSteps: number;
  visible: boolean;
  completed: boolean;
  exited: boolean;
}

export interface GuideSessionInput {
  projectId: string;
  activityId: string;
  totalSteps: number;
  initialStep?: number;
}

export function clampGuideStep(step: number, totalSteps: number): number {
  const safeTotal = Math.max(1, Math.floor(Number.isFinite(totalSteps) ? totalSteps : 1));
  const safeStep = Number.isFinite(step) ? Math.floor(step) : 1;
  return Math.min(safeTotal, Math.max(1, safeStep));
}

/** Small session-only state machine. It owns no artwork or persistence state. */
export class GuideSessionController {
  private state: GuideSessionState;

  public constructor(input: GuideSessionInput) {
    const totalSteps = Number.isFinite(input.totalSteps)
      ? Math.max(1, Math.floor(input.totalSteps))
      : 1;
    this.state = {
      projectId: input.projectId,
      activityId: input.activityId,
      step: clampGuideStep(input.initialStep ?? 1, totalSteps),
      totalSteps,
      visible: true,
      completed: false,
      exited: false,
    };
  }

  public getState(): GuideSessionState {
    return { ...this.state };
  }

  public goTo(step: number): GuideSessionState {
    if (!this.state.exited) this.state.step = clampGuideStep(step, this.state.totalSteps);
    return this.getState();
  }

  public previous(): GuideSessionState {
    if (!this.state.exited) this.state.step = Math.max(1, this.state.step - 1);
    return this.getState();
  }

  public next(): GuideSessionState {
    if (this.state.exited) return this.getState();
    if (this.state.step >= this.state.totalSteps) {
      this.state.completed = true;
      this.state.exited = true;
    } else {
      this.state.step += 1;
    }
    return this.getState();
  }

  public restart(): GuideSessionState {
    if (!this.state.exited) {
      this.state.step = 1;
      this.state.completed = false;
      this.state.visible = true;
    }
    return this.getState();
  }

  public toggleGuide(): GuideSessionState {
    if (!this.state.exited) this.state.visible = !this.state.visible;
    return this.getState();
  }

  public exit(): GuideSessionState {
    this.state.exited = true;
    return this.getState();
  }
}
